import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const publicPaths = ['/', '/terms', '/privacy', '/refund-policy', '/payment/success'];
      if (!publicPaths.includes(window.location.pathname)) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    const message = error.response?.data?.message || error.message || 'Request failed';
    toast.error(message);
    return Promise.reject(error);
  }
);

export const auth = {
  login: (data) => api.post('/auth/login', data),
  registerSubscription: (data) => api.post('/auth/register-subscription', data),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
  updateUsername: (data) => api.put('/auth/update-username', data),
  maintenanceStatus: () => api.get('/maintenance-status'),
};

export const telegramSubscription = {
  register: (data) => api.post('/auth/register-subscription', data),
  createOrder: (data) => api.post('/subscriptions/telegram/create-order', data || {}),
  verifyPayment: (data) => api.post('/subscriptions/telegram/verify-payment', data),
  myStatus: () => api.get('/subscriptions/telegram/my-status'),
  myHistory: () => api.get('/subscriptions/my-history'),
  applyCoupon: (code) => api.post('/subscriptions/coupons/apply', { code }),
  adminSubscribers: () => api.get('/subscriptions/admin/subscribers-list'),
  adminCoupons: () => api.get('/subscriptions/admin/coupons'),
  createAdminCoupon: (data) => api.post('/subscriptions/admin/coupons', data),
  toggleAdminCoupon: (id) => api.put(`/subscriptions/admin/coupons/${id}/toggle`),
  deleteAdminCoupon: (id) => api.delete(`/subscriptions/admin/coupons/${id}`),
};

export const dashboard = {
  get: () => api.get('/dashboard'),
  recentTrades: (params) => api.get('/dashboard/recent-trades', { params }),
  pnlChart: (params) => api.get('/dashboard/pnl-chart', { params }),
};

export const bot = {
  status: () => api.get('/bot/status'),
  start: () => api.post('/bot/start'),
  stop: () => api.post('/bot/stop'),
  switchMode: (mode) => api.post('/bot/switch-mode', { mode }),
  systemLogs: (params) => api.get('/bot/system-logs', { params }),
  auditLogs: (params) => api.get('/bot/audit-logs', { params }),
};

export const settings = {
  getAll: () => api.get('/settings'),
  getGroup: (group) => api.get(`/settings/group/${group}`),
  update: (data) => api.put('/settings', data),
  getStrategies: () => api.get('/settings/strategies'),
  updateStrategy: (id, data) => api.put(`/settings/strategies/${id}`, data),
};

export const trading = {
  paperPositions: (params) => api.get('/trading/paper/positions', { params }),
  paperOrders: (params) => api.get('/trading/paper/orders', { params }),
  paperAccount: () => api.get('/trading/paper/account'),
  resetPaper: (data) => api.post('/trading/paper/reset', data),
  livePositions: (params) => api.get('/trading/live/positions', { params }),
  liveOrders: (params) => api.get('/trading/live/orders', { params }),
  history: (params) => api.get('/trading/history', { params }),
  signals: (params) => api.get('/trading/signals', { params }),
  signalLog: () => api.get('/trading/signal-log'),
  marketData: (params) => api.get('/trading/market-data', { params }),
  optionChain: (params) => api.get('/trading/option-chain', { params }),
  dailyPnl: (params) => api.get('/trading/daily-pnl', { params }),
  closePosition: (data) => api.post('/trading/close-position', data),
  clearHistory: () => api.post('/trading/clear-history'),
  stockRecommendations: (params) => api.get('/trading/stock-recommendations', { params }),
  buyStock: (data) => api.post('/trading/stock-buy', data),
  sellStock: (data) => api.post('/trading/stock-sell', data),
  equityOrders: (params) => api.get('/trading/equity-orders', { params }),
  equityOrderDetail: (id) => api.get(`/trading/equity-orders/${id}`),
  equityOrderLifecycleChart: (id) => api.get(`/trading/equity-orders/${id}/lifecycle-chart`),
  tradeLifecycleChart: (id) => api.get(`/trading/trades/${id}/lifecycle-chart`),
  equityLivePrices: () => api.get('/trading/equity-live-prices'),
  updateEquityStopLoss: (id, stop_loss) => api.put(`/trading/equity-orders/${id}/stop-loss`, { stop_loss }),
  zerodhaPortfolio: () => api.get('/trading/zerodha-portfolio'),
  syncZerodhaPortfolio: () => api.post('/trading/zerodha-portfolio/sync'),
  activateEquityMonitoring: (id, stop_loss) => api.post(`/trading/equity-orders/${id}/activate`, stop_loss ? { stop_loss } : {}),
  manualBuy: (data) => api.post('/trading/manual-buy', data),
  capitalSummary: () => api.get('/trading/capital-summary'),
};

export const equityEngine = {
  status: () => api.get('/equity-engine/status'),
  signals: (params) => api.get('/equity-engine/signals', { params }),
  evaluations: (params) => api.get('/equity-engine/evaluations', { params }),
  setEnabled: (enabled) => api.post('/equity-engine/enable', { enabled }),
  backfill: () => api.post('/equity-engine/backfill'),
  runNow: () => api.post('/equity-engine/run-now'),
};

export const testPositions = {
  list: () => api.get('/test-positions'),
  create: (data) => api.post('/test-positions', data),
  updatePrice: (id, price) => api.put(`/test-positions/${id}/price`, { price }),
  remove: (id) => api.delete(`/test-positions/${id}`),
  resetAll: () => api.post('/test-positions/reset'),
};

export const testingApi = {
  dashboard: () => api.get('/testing/dashboard'),
  allOrders: (params) => api.get('/testing/all-orders', { params }),
  simulateTrade: (data) => api.post('/testing/simulate-trade', data),
  testPositions: testPositions,
};

export const zerodha = {
  status: () => api.get('/zerodha/status'),
  loginUrl: () => api.get('/zerodha/login-url'),
  connect: (request_token) => api.post('/zerodha/connect', { request_token }),
  disconnect: () => api.post('/zerodha/disconnect'),
};

export const backtest = {
  runs: () => api.get('/backtest'),
  create: (data) => api.post('/backtest', data),
  get: (id) => api.get(`/backtest/${id}`),
  delete: (id) => api.delete(`/backtest/${id}`),
};

export const reports = {
  list: () => api.get('/reports'),
  create: (data) => api.post('/reports', data),
  get: (id) => api.get(`/reports/${id}`),
  download: (id, format) => window.open(`${API_BASE}/reports/${id}/download/${format}?token=${localStorage.getItem('token')}`),
};

export const activityLogs = {
  list: (params) => api.get('/activity-logs', { params }),
  stats: () => api.get('/activity-logs/stats/summary'),
  export: (params) => api.get('/activity-logs/export/csv', { params, responseType: 'blob' }),
};

export const agentCenter = {
  overview: () => api.get('/agent-center/overview'),
  trader: () => api.get('/agent-center/trader'),
  dev: () => api.get('/agent-center/dev'),
  tester: () => api.get('/agent-center/tester'),
  realityChecks: () => api.get('/agent-center/reality-checks'),
  theses: () => api.get('/agent-center/theses'),
  riskAnalytics: () => api.get('/agent-center/risk-analytics'),
  tasks: () => api.get('/agent-center/tasks'),
  decisions: () => api.get('/agent-center/decisions'),
  devRequests: () => api.get('/agent-center/dev-requests'),
  devRequestAction: (id, action) => api.post(`/agent-center/dev-requests/${id}/action`, { action }),
  bugs: () => api.get('/agent-center/bugs'),
  testResults: () => api.get('/agent-center/test-results'),
  learning: () => api.get('/agent-center/learning'),
  activity: () => api.get('/agent-center/activity'),
  triggerCycle: () => api.post('/agent-center/trigger-cycle'),
};

export const notifications = {
  list: (params) => api.get('/notifications', { params }),
  getById: (id) => api.get(`/notifications/${id}`),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.put('/notifications/settings', data),
  test: (data) => api.post('/notifications/test', data),
  getTelegramStatus: () => api.get('/notifications/telegram/status'),
  detectTelegramChatId: () => api.post('/notifications/telegram/detect'),
  testTelegram: (data) => api.post('/notifications/telegram/test', data),
};

export default api;
